import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import {
    CustomDetailComponent,
    DataService,
    NotificationService,
    Promotion,
    SharedModule,
} from '@vendure/admin-ui/core';
import { SendPromoNotificationMutation, SendPromoNotificationMutationVariables } from '../generated-types';
import { UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { FormBuilder, Validators } from '@angular/forms';
import { SEND_PROMO_NOTIFICATION } from '../graphql';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'send-promo-notification',
    templateUrl: './send-promo-notification.component.html',
    styleUrls: ['./send-promo-notification.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [SharedModule],
})
export class SendPromoNotificationComponent implements CustomDetailComponent, OnInit {
    entity$: Observable<Promotion>;
    detailForm: UntypedFormGroup;
    imageURL: string;
    isCreatePage: boolean = false;
    promotionId: string;
    isEnabled: boolean = false;
    isAdminAccepted: boolean = false;

    constructor(
        private formBuilder: FormBuilder,
        private notificationService: NotificationService,
        private dataService: DataService,
        private route: ActivatedRoute,
    ) {}

    ngOnInit() {
        this.detailForm = this.formBuilder.group({
            title: [],
            body: [],
            includeBanner: [false],
        });
        this.route.url.subscribe(urlSegments => {
            this.isCreatePage = urlSegments.some(segment => segment.path.includes('create'));
        });
        this.entity$.subscribe(promotion => {
            if (promotion) {
                this.promotionId = promotion.id;
                this.isEnabled = promotion.enabled;
                this.isAdminAccepted = promotion.customFields?.adminAccepted;
                this.imageURL = promotion.customFields?.banner?.source;
                this.detailForm.patchValue({
                    title: promotion.name,
                    body: promotion.description,
                });
            }
        });
    }

    sendNotification() {
        if (this.detailForm?.valid) {
            const title = this.detailForm.get('title')?.value;
            const body = this.detailForm.get('body')?.value;
            const includeBanner = this.detailForm.get('includeBanner')?.value;

            if (!this.isEnabled) {
                this.notificationService.error(
                    'Promotion is not enabled. Please enable it to send notification.',
                );
                return;
            }

            if (!this.isAdminAccepted) {
                this.notificationService.error(
                    'Promotion is not accepted by admin. Please get it accepted it to send notification.',
                );
                return;
            }

            if (!title) {
                this.notificationService.error('Please fill in title at least to send notification.');
                return;
            }

            if (includeBanner && !this.imageURL) {
                this.notificationService.error(
                    'Please upload a banner image and update in order to include in the notification.',
                );
                return;
            }

            // You can later use these values with a mutation or service
            // Example payload structure:
            const variables: SendPromoNotificationMutationVariables = {
                input: {
                    title,
                    promotionId: this.promotionId,
                    body,
                    imageUrl: this.imageURL,
                },
            };

            this.dataService
                .mutate<SendPromoNotificationMutation, SendPromoNotificationMutationVariables>(
                    SEND_PROMO_NOTIFICATION,
                    variables,
                )
                .subscribe({
                    next: response => {
                        if (response?.sendPromoNotification?.success) {
                            this.notificationService.success('Notification sent successfully!');
                        } else {
                            this.notificationService.error(
                                'Failed to send notification: ' + response?.sendPromoNotification?.message,
                            );
                        }
                    },
                    error: error => {
                        this.notificationService.error('Error sending notification: ' + error.message);
                    },
                });
        } else {
            this.notificationService.error('Form is invalid. Please fill in all required fields.');
            return;
        }
    }
}
